import { useState } from 'react'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import FormControlLabel from '@mui/material/FormControlLabel'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import { problemMessage } from '../../api/baseApi'
import { useCreatePlanMutation, useListPlansQuery, useUpdatePlanMutation, type Plan } from '../../api/operator'
import { formatMoney, parseMoney } from '../../model/money'
import { EmptyState, Loading, QueryError } from '../../ui/QueryState'
import { centsText, MoneyField, OperatorHeader } from './common'
import { shown } from '../../api/useIntent'

/**
 * The price list. A plan grants nothing and nothing enforces it: it is a
 * name and a price per interval, copied onto an agreement when a tenant
 * is put on it — so a change here reaches new agreements only.
 */
export function OperatorPlans() {
  const plans = useListPlansQuery()
  const [editing, setEditing] = useState<Plan | 'new' | null>(null)

  if (plans.isLoading) return <Loading label="Loading plans…" />
  if (plans.error) return <QueryError error={plans.error} title="Could not load plans" />
  const list = plans.data?.plans ?? []

  return (
    <Stack spacing={3}>
      <OperatorHeader
        title="Plans"
        subtitle="What a tenant can be put on, and what it costs. A price change applies to agreements made afterwards."
        actions={
          <Button variant="contained" onClick={() => setEditing('new')}>
            New plan
          </Button>
        }
      />
      {list.length === 0 ? (
        <EmptyState title="No plans yet" />
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Plan</TableCell>
                <TableCell align="right">Monthly</TableCell>
                <TableCell align="right">Annually</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {list.map((plan) => (
                <TableRow key={plan.id} hover>
                  <TableCell>
                    {plan.name} {!plan.active && <Chip size="small" label="not offered" sx={{ ml: 1 }} />}
                  </TableCell>
                  <TableCell align="right">{formatMoney(plan.monthly)}</TableCell>
                  <TableCell align="right">{formatMoney(plan.annual)}</TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => setEditing(plan)}>
                      Edit
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {editing && <PlanDialog plan={editing === 'new' ? undefined : editing} onClose={() => setEditing(null)} />}
    </Stack>
  )
}

function PlanDialog({ plan, onClose }: { plan?: Plan; onClose: () => void }) {
  const [create, creating] = useCreatePlanMutation()
  const [update, updating] = useUpdatePlanMutation()
  const [name, setName] = useState(plan?.name ?? '')
  const [monthly, setMonthly] = useState(centsText(plan?.monthly))
  const [annual, setAnnual] = useState(centsText(plan?.annual))
  const [active, setActive] = useState(plan?.active ?? true)
  const error = creating.error ?? updating.error
  const monthlyCents = parseMoney(monthly)
  const annualCents = parseMoney(annual)

  const save = async () => {
    const planInput = { name: name.trim(), monthly: monthlyCents ?? 0, annual: annualCents ?? 0, active }
    await (plan ? update({ planId: plan.id, planInput }) : create({ planInput })).unwrap()
    onClose()
  }

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{plan ? `Edit ${plan.name}` : 'New plan'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {plan && <DialogContentText>Tenants already on this plan keep the price they agreed.</DialogContentText>}
          {Boolean(error) && <Alert severity="error">{problemMessage(error, 'The plan was not saved.')}</Alert>}
          <TextField label="Name" value={name} onChange={(event) => setName(event.target.value)} autoFocus />
          <MoneyField label="Monthly price" value={monthly} onChange={setMonthly} />
          <MoneyField label="Annual price" value={annual} onChange={setAnnual} />
          <FormControlLabel control={<Switch checked={active} onChange={(event) => setActive(event.target.checked)} />} label="Offered to new tenants" />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!name.trim() || monthlyCents === null || annualCents === null || creating.isLoading || updating.isLoading}
          onClick={() => save().catch(shown)}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}
